import {
  mepGetDefaultActivationLevel,
  MyEmergencyPlanRegisterFormSchema,
  myEmergencyPlanFormFirstStageId,
  MyEmergencyPlanFormSchema,
  myEmergencyPlanFormSchema,
  MyEmergencyPlanRepositoryImpl,
  MyEmergencyPlanStageKeys,
  MyEmergencyPlanStages,
  MyEmergencyPlanStatus,
  myEmergencyPlanSteps,
  MyEmergencyPlanGetPlanDeepLinkSchema,
  MetricEventsRepository,
  LocalGeocodeRepository,
  SuburbsGeocodingLocalDatasource,
} from '@/features';
import { defineStore } from 'pinia';
import { useForm } from 'vee-validate';
import { computed, ref, watchEffect } from 'vue';

import { toTypedSchema } from '@vee-validate/zod';
import { container } from 'tsyringe';
import { DatasourceSingleton, GoogleAnalyticsEvents, toTitleCase } from '@/common';
import { fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import { useGtm } from '@gtm-support/vue-gtm';

const initialValues: Partial<MyEmergencyPlanFormSchema> = {};

export const useMyEmergencyPlanStore = defineStore(
  'myEmergencyPlanStore',
  () => {
    const dataSource = container.resolve(DatasourceSingleton).mep;
    const mepRepository = new MyEmergencyPlanRepositoryImpl(dataSource);
    const metricEventsRepository = new MetricEventsRepository(container.resolve(DatasourceSingleton).metricEvents);
    const geocodeRepository = new LocalGeocodeRepository(new SuburbsGeocodingLocalDatasource());
    const gtm = useGtm();

    // Getters
    const planStatus = ref(MyEmergencyPlanStatus.NotStarted);
    const navigationStack = ref<MyEmergencyPlanStageKeys[]>([]);
    const editingStepId = ref<number | null>(null);
    const isStageValid = ref<boolean | null>(null);
    const isDownloadingPlanPdf = ref(false);
    const isSendingPdfEmail = ref(false);
    const isRegisteringForEvent = ref(false);
    const allowSafeNavigation = ref(false);
    const seenWarningSlides = ref(false);

    const nextStageId = ref<MyEmergencyPlanStageKeys | null>(null);

    //* Used for persistence only
    const _syncedValues = ref({});
    const form = useForm<MyEmergencyPlanFormSchema>({
      validationSchema: toTypedSchema(myEmergencyPlanFormSchema),
    });

    //* Computed
    const currentStageId = computed(() => navigationStack.value.at(-1) ?? myEmergencyPlanFormFirstStageId);
    const currentStage = computed(() => MyEmergencyPlanStages[currentStageId.value]);
    const currentStepId = computed(() => currentStage.value.stepId);
    const currentStep = computed(() => myEmergencyPlanSteps[currentStepId.value - 1]);
    const nextStepId = computed(() => currentStepId.value + 1);
    const nextStep = computed(() => myEmergencyPlanSteps[nextStepId.value - 1] || null);
    const isEditing = computed(() => editingStepId.value !== null);
    const isStageNextStep = computed(() => nextStep.value?.startStageId === nextStageId.value);

    //* Actions
    const nextStage = () => {
      if (!nextStageId.value) return;

      if (isEditing.value && isStageNextStep.value) {
        return exitEditingMode();
      } else {
        navigationStack.value.push(nextStageId.value);
      }

      nextStageId.value = null;
      isStageValid.value = null;
    };

    const popStage = () => {
      const isStageStartOfStep = currentStep!.value!.startStageId === currentStageId.value;

      if (isEditing.value && isStageStartOfStep) {
        exitEditingMode();
        return;
      }

      if (navigationStack.value.length <= 1) {
        setPlanStatus(MyEmergencyPlanStatus.NotStarted);
        return;
      }

      if (planStatus.value === MyEmergencyPlanStatus.Completed) {
        planStatus.value = MyEmergencyPlanStatus.InProgress;
        return;
      }

      navigationStack.value.pop();
      isStageValid.value = null;
    };

    const exitWithoutSaving = () => {
      setPlanStatus(MyEmergencyPlanStatus.NotStarted);
      form.resetForm({ values: initialValues });
      seenWarningSlides.value = false;
    };

    const editStep = (stepId: number) => {
      const stepStartStageId = myEmergencyPlanSteps[stepId - 1].startStageId;

      navigationStack.value = [stepStartStageId];

      editingStepId.value = stepId;
      setPlanStatus(MyEmergencyPlanStatus.Editing);
    };

    const exitEditingMode = () => {
      setPlanStatus(MyEmergencyPlanStatus.ViewingPlan);
      editingStepId.value = null;
    };

    const setPlanStatus = (newPlanStatus: MyEmergencyPlanStatus) => {
      if (newPlanStatus === MyEmergencyPlanStatus.NotStarted) {
        navigationStack.value = [];
      } else if (newPlanStatus === MyEmergencyPlanStatus.InProgress) {
        navigationStack.value = [myEmergencyPlanFormFirstStageId];
        gtm?.trackEvent(GoogleAnalyticsEvents.usageEvent({ name: 'mep_plan_commenced', category: 'emergency_plan' }));
        metricEventsRepository.createEvent({ name: 'MEP_COMMENCED' });
      } else if (newPlanStatus === MyEmergencyPlanStatus.Completed) {
        gtm?.trackEvent(GoogleAnalyticsEvents.usageEvent({ name: 'mep_plan_completed', category: 'emergency_plan' }));
        metricEventsRepository.createEvent({
          name: 'MEP_COMPLETED',
          data: {
            suburb: toTitleCase(geocodeRepository.getGeocodingById(form.values.ref5Suburb).name),
          },
        });
        if (isEditing.value) exitEditingMode();
      } else if (newPlanStatus === MyEmergencyPlanStatus.ViewingPlan) {
        navigationStack.value = [];
        isStageValid.value = null;
      }

      // Will override user inputted activation level
      form.setFieldValue('activationLevel', mepGetDefaultActivationLevel(form.values));

      planStatus.value = newPlanStatus;
    };

    /**
     * Downloads the emergency plan as a PDF
     * @returns true if the download was initiated successfully, false otherwise, undefined if the operation was aborted (e.g. already downloading)
     */
    const downloadPlan = async (): Promise<boolean | undefined> => {
      if (isDownloadingPlanPdf.value) return;
      isDownloadingPlanPdf.value = true;
      const maybeUrl = await mepRepository.getMepDownloadUrl({
        ...form.values,
        'ref29.1': form.values['ref29_1'],
        g5Address: form.values.g5.address,
        g5Route: form.values.g5.route,
      });
      pipe(
        maybeUrl,
        fold(
          () => {
            // TODO: Handle error case (toast?)
          },
          (url) => {
            _safeNavigation(url);
          }
        )
      );
      isDownloadingPlanPdf.value = false;

      return maybeUrl._tag === 'Right';
    };

    /**
     * Sends the emergency plan by email
     * @param email The recipient email address
     * @returns true if the email was sent successfully, false otherwise, undefined if the operation was aborted (e.g. already sending)
     */
    const sendPlanByEmail = async (email: string): Promise<boolean | undefined> => {
      if (isSendingPdfEmail.value) return;
      isSendingPdfEmail.value = true;
      const maybeResult = await mepRepository.sendMepByEmail({
        data: { ...form.values, 'ref29.1': form.values['ref29_1'], g5Address: form.values.g5.address, g5Route: form.values.g5.route },
        email,
      });
      pipe(
        maybeResult,
        fold(
          () => {
            // TODO: Handle error case (toast?)
          },
          () => {
            // TODO: Handle success case (toast?)
          }
        )
      );
      isSendingPdfEmail.value = false;

      return maybeResult._tag === 'Right';
    };

    const registerForPreparednessEvent = async (form: MyEmergencyPlanRegisterFormSchema): Promise<boolean | undefined> => {
      isRegisteringForEvent.value = true;
      const maybeResult = await mepRepository.registerForPreparednessEvent(form);
      pipe(
        maybeResult,
        fold(
          () => {
            // TODO: Handle error case (toast?)
          },
          () => {
            // TODO: Handle success case (toast?)
          }
        )
      );
      isRegisteringForEvent.value = false;

      return maybeResult._tag === 'Right';
    };

    const getPlanDeepLink = async (data: MyEmergencyPlanGetPlanDeepLinkSchema): Promise<string | undefined> => {
      const maybeUrl = await mepRepository.getPlanDeepLink(data);
      return pipe(
        maybeUrl,
        fold(
          () => {
            // TODO: Handle error case (toast?)
            return undefined;
          },
          (url) => url
        )
      );
    };

    const _safeNavigation = (href: string) => {
      allowSafeNavigation.value = true;
      window.location.href = href;
      allowSafeNavigation.value = false;
    };

    //* Watchers
    watchEffect(() => {
      _syncedValues.value = { ...form.values };
    });

    return {
      // Getters
      planStatus,
      navigationStack,
      form,
      editingStepId,
      nextStageId,
      isStageValid,
      isDownloadingPlanPdf,
      isSendingPdfEmail,
      isRegisteringForEvent,
      allowSafeNavigation,
      seenWarningSlides,
      // Computed
      currentStageId,
      currentStage,
      currentStepId,
      currentStep,
      nextStepId,
      nextStep,
      isEditing,
      // Actions
      setPlanStatus,
      nextStage,
      popStage,
      exitWithoutSaving,
      editStep,
      isStageNextStep,
      exitEditingMode,
      downloadPlan,
      sendPlanByEmail,
      registerForPreparednessEvent,
      getPlanDeepLink,
      /**
       * Used for persistence only
       * */
      _syncedValues,
    };
  },
  {
    persist: import.meta.env.VITE_MEP_PERSIST_STATE === 'true' && {
      storage: sessionStorage,
      afterRestore: (context) => {
        const form = context.store.form as ReturnType<typeof useForm>;
        const syncedValues = context.store._syncedValues;

        form.setValues(syncedValues ?? initialValues);
        context.store.seenWarningSlides = false;
      },
    },
  }
);
